import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Box, Typography } from '@mui/material';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DistanceUnits } from '../types';
import { formatDistance } from '../geo/format';

const GOLD = '#f5b942';
const baseUrl = process.env.REACT_APP_API_URL || '/api';

interface ElevationPoint {
  longitude: number;
  latitude: number;
  distance: number;
  elevation: number | null;
}

interface ElevationProfileProps {
  geometry: GeoJSON.LineString | null;
  units: DistanceUnits;
  /** Moves a marker along the route as the operator scrubs the chart. */
  onHoverPoint?: (point: { longitude: number; latitude: number } | null) => void;
}

const ElevationProfile: React.FC<ElevationProfileProps> = ({ geometry, units, onHoverPoint }) => {
  const [points, setPoints] = useState<ElevationPoint[]>([]);
  const [ascent, setAscent] = useState(0);
  const [descent, setDescent] = useState(0);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle');
  const lastKey = useRef<string>('');

  const key = useMemo(() => {
    const coords = geometry?.coordinates || [];
    if (coords.length < 2) return '';
    const first = coords[0];
    const last = coords[coords.length - 1];
    return `${coords.length}:${first[0].toFixed(4)},${first[1].toFixed(4)}:${last[0].toFixed(4)},${last[1].toFixed(4)}`;
  }, [geometry]);

  useEffect(() => {
    if (!key || !geometry) {
      setPoints([]);
      setStatus('idle');
      return undefined;
    }
    if (key === lastKey.current) return undefined;
    lastKey.current = key;

    const controller = new AbortController();
    setStatus('loading');
    axios.post(
      `${baseUrl}/routing/elevation`,
      { coordinates: geometry.coordinates, samples: 120 },
      {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || ''}` },
        signal: controller.signal,
      },
    ).then((res) => {
      setPoints(res.data.points || []);
      setAscent(res.data.ascent || 0);
      setDescent(res.data.descent || 0);
      setStatus('ready');
    }).catch((e) => {
      if (axios.isCancel(e) || e?.name === 'CanceledError') return;
      setStatus('failed');
    });

    return () => controller.abort();
  }, [key, geometry]);

  const chartData = useMemo(
    () => points
      .filter((p) => p.elevation != null)
      .map((p) => ({
        distance: p.distance,
        elevation: p.elevation as number,
        longitude: p.longitude,
        latitude: p.latitude,
      })),
    [points],
  );

  if (!key) return null;

  if (status === 'loading') {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">Reading terrain…</Typography>
      </Box>
    );
  }

  if (status === 'failed' || chartData.length < 2) {
    return status === 'failed' ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Terrain data unavailable for this route.
      </Typography>
    ) : null;
  }

  const unitLabel = units === 'mi' ? 'ft' : 'm';
  const toDisplay = (meters: number) => (units === 'mi' ? Math.round(meters * 3.28084) : Math.round(meters));

  return (
    <Box sx={{ mt: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.25 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>Elevation</Typography>
        <Typography variant="caption" color="text.secondary">
          ▲ {toDisplay(ascent)} {unitLabel} · ▼ {toDisplay(descent)} {unitLabel}
        </Typography>
      </Box>
      <Box
        sx={{ height: 96, mx: -0.5 }}
        onMouseLeave={() => onHoverPoint?.(null)}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
            onMouseMove={(e: any) => {
              const point = e?.activePayload?.[0]?.payload;
              if (point) onHoverPoint?.({ longitude: point.longitude, latitude: point.latitude });
            }}
          >
            <defs>
              <linearGradient id="rp-elev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={GOLD} stopOpacity={0.5} />
                <stop offset="100%" stopColor={GOLD} stopOpacity={0.04} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="distance"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(v) => formatDistance(v, units)}
              tick={{ fontSize: 9 }}
              stroke="rgba(255,255,255,0.35)"
              minTickGap={40}
            />
            <YAxis
              tickFormatter={(v) => String(toDisplay(v))}
              tick={{ fontSize: 9 }}
              stroke="rgba(255,255,255,0.35)"
              width={32}
              domain={['dataMin - 10', 'dataMax + 10']}
            />
            <Tooltip
              contentStyle={{
                background: 'rgba(11,16,23,0.95)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 6,
                fontSize: 11,
              }}
              labelFormatter={(v) => formatDistance(Number(v), units)}
              formatter={(v: any) => [`${toDisplay(Number(v))} ${unitLabel}`, 'Elevation']}
            />
            <Area
              type="monotone"
              dataKey="elevation"
              stroke={GOLD}
              strokeWidth={1.5}
              fill="url(#rp-elev)"
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default ElevationProfile;
